/* Tabbed Product Carousel - v9.0 (Dawn-safe)
   - Mobile mini-gallery becomes static via CSS (JS remains desktop-friendly)
   - Main carousel: centered pager dots (per tab)
   - Quick view: swipe + arrows
   - Quick view: auto-close after Add-to-Cart (Dawn pub/sub + fallback)
*/

(function () {
  const INSTANCES = (window.__TabbedCarouselInstances = window.__TabbedCarouselInstances || new Map());

  class TabbedCarousel {
    constructor(container) {
      this.container = container;
      this.modalId = container?.dataset?.modalId;
      this.modal = this.modalId ? document.getElementById(this.modalId) : null;
      if (!this.container || !this.modal) return;

      this._onContainerClick = this.onContainerClick.bind(this);
      this._onModalClick = this.onModalClick.bind(this);
      this._onKeyDown = this.onKeyDown.bind(this);
      this._onResize = this.onResize.bind(this);

      this._onGalleryPointerDown = this.onGalleryPointerDown.bind(this);
      this._onGalleryPointerMove = this.onGalleryPointerMove.bind(this);
      this._onGalleryPointerUp = this.onGalleryPointerUp.bind(this);

      this._dragState = { active: false, startX: 0, track: null };
      this._lastFocus = null;

      this.container.addEventListener('click', this._onContainerClick);
      this.modal.addEventListener('click', this._onModalClick);
      document.addEventListener('keydown', this._onKeyDown);
      window.addEventListener('resize', this._onResize);

      // Guard: don't open QV if the user was swiping a desktop mini-gallery
      this.container.addEventListener('pointerdown', this._onGalleryPointerDown, { passive: true });
      this.container.addEventListener('pointermove', this._onGalleryPointerMove, { passive: true });
      this.container.addEventListener('pointerup', this._onGalleryPointerUp, { passive: true });
      this.container.addEventListener('pointercancel', this._onGalleryPointerUp, { passive: true });

      this.initToastListener();
      this.attachGalleryScrollSync();
      this.buildPagerForActiveTab();
      this.attachModalAutoCloseOnSubmit();
    }

    destroy() {
      this.container.removeEventListener('click', this._onContainerClick);
      this.modal.removeEventListener('click', this._onModalClick);
      document.removeEventListener('keydown', this._onKeyDown);
      window.removeEventListener('resize', this._onResize);

      this.container.removeEventListener('pointerdown', this._onGalleryPointerDown);
      this.container.removeEventListener('pointermove', this._onGalleryPointerMove);
      this.container.removeEventListener('pointerup', this._onGalleryPointerUp);
      this.container.removeEventListener('pointercancel', this._onGalleryPointerUp);

      if (this._unsub) {
        try { this._unsub(); } catch (_) {}
      }
    }

    /* ---------- TOAST ---------- */
    showToast(message) {
      const toast = this.container.querySelector('.tab-carousel__toast');
      if (!toast) return;
      toast.textContent = message;
      toast.classList.add('show');
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => toast.classList.remove('show'), 2500);
    }

    /* ---------- QUICK VIEW AUTO-CLOSE (Dawn PubSub) ---------- */
    initToastListener() {
      if (window.PUB_SUB_EVENTS && window.subscribe) {
        this._unsub = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (!this.modal.classList.contains('open')) return;
          if (event?.source === 'product-form') {
            this.showToast('Added to Cart!');
            this.closeModal();
          }
        });
      }
    }

    /* ---------- QUICK VIEW AUTO-CLOSE (Fallback if PubSub differs) ---------- */
    attachModalAutoCloseOnSubmit() {
      const form = this.modal.querySelector('form[data-type="add-to-cart-form"]');
      if (!form) return;

      form.addEventListener('submit', () => {
        if (!this.modal.classList.contains('open')) return;

        clearTimeout(this._autoCloseTimer);
        this._autoCloseTimer = setTimeout(() => {
          const err = this.modal.querySelector('.product-form__error-message-wrapper');
          const hasError = err && !err.hasAttribute('hidden');
          if (!hasError) this.closeModal();
        }, 450);
      });
    }

    /* ---------- GLOBAL EVENTS ---------- */
    onKeyDown(e) {
      if (e.key === 'Escape' && this.modal.classList.contains('open')) {
        this.closeModal();
      }
    }

    onResize() {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this.buildPagerForActiveTab(), 140);
    }

    /* ---------- MODAL ---------- */
    onModalClick(e) {
      if (e.target.closest('.qv-trigger-close')) {
        this.closeModal();
        return;
      }

      const arrow = e.target.closest('.qv-arrow');
      if (arrow) {
        const track = this.modal.querySelector('.qv-slider-track');
        if (!track) return;
        const dir = parseInt(arrow.dataset.dir, 10) || 0;
        track.scrollBy({ left: track.clientWidth * dir, behavior: 'smooth' });
      }
    }

    /* ---------- POINTER (desktop mini-gallery swipe guard) ---------- */
    onGalleryPointerDown(e) {
      const track = e.target.closest('[data-gallery-track]');
      if (!track) return;
      this._dragState.active = true;
      this._dragState.startX = e.clientX;
      this._dragState.track = track;
      track.dataset.dragging = 'false';
    }

    onGalleryPointerMove(e) {
      if (!this._dragState.active || !this._dragState.track) return;
      const dx = Math.abs(e.clientX - this._dragState.startX);
      if (dx > 6) this._dragState.track.dataset.dragging = 'true';
    }

    onGalleryPointerUp() {
      this._dragState.active = false;
      this._dragState.track = null;
    }

    /* ---------- CONTAINER CLICK ---------- */
    onContainerClick(e) {
      // Tabs
      const tabBtn = e.target.closest('.tab-carousel__tab-btn');
      if (tabBtn) {
        this.activateTab(tabBtn);
        return;
      }

      // Mini-gallery dot click (desktop)
      const gDot = e.target.closest('.tab-carousel__gallery-dot');
      if (gDot) {
        e.preventDefault();
        e.stopPropagation();
        this.onGalleryDotClick(gDot);
        return;
      }

      // Main pager dots
      const pDot = e.target.closest('.tab-carousel__page-dot');
      if (pDot) {
        e.preventDefault();
        this.onMainPagerDotClick(pDot);
        return;
      }

      // Mini-gallery arrows (desktop)
      const imgNav = e.target.closest('.tab-carousel__img-nav');
      if (imgNav) {
        e.preventDefault();
        e.stopPropagation();
        this.scrollCardGallery(imgNav);
        return;
      }

      // Quick view triggers
      const trigger = e.target.closest('[data-qv-trigger]');
      if (trigger) {
        const gallery = trigger.classList.contains('tab-carousel__gallery')
          ? trigger
          : trigger.closest('.tab-carousel__card')?.querySelector('.tab-carousel__gallery');

        const track = gallery?.querySelector('[data-gallery-track]');
        if (track && track.dataset.dragging === 'true') {
          track.dataset.dragging = 'false';
          return;
        }

        if (trigger.hasAttribute('disabled')) return;
        const id = trigger.dataset.id;
        if (id) this.openModal(id);
      }
    }

    /* ---------- TABS ---------- */
    activateTab(btn) {
      const tabs = this.container.querySelectorAll('.tab-carousel__tab-btn');
      const contents = this.container.querySelectorAll('.tab-carousel__content');

      tabs.forEach((b) => {
        const isActive = b === btn;
        b.classList.toggle('active', isActive);
        b.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      const targetId = btn.dataset.target;
      contents.forEach((c) => c.classList.toggle('active', c.id === targetId));

      requestAnimationFrame(() => this.buildPagerForActiveTab());
    }

    /* ---------- MAIN PAGER ---------- */
    buildPagerForActiveTab() {
      const active = this.container.querySelector('.tab-carousel__content.active');
      if (!active) return;

      const slider = active.querySelector('[data-main-slider]');
      const pager = active.querySelector('[data-main-pager]');
      if (!slider || !pager) return;

      const cards = slider.querySelectorAll('.tab-carousel__card');
      if (!cards.length) {
        pager.hidden = true;
        pager.innerHTML = '';
        return;
      }

      const first = cards[0];
      const cardWidth = first.getBoundingClientRect().width || 280;
      const gap = parseFloat(getComputedStyle(slider).gap || '20') || 20;
      const step = cardWidth + gap;

      const cardsPerView = Math.max(1, Math.floor((slider.clientWidth + gap) / step));
      const pages = Math.ceil(cards.length / cardsPerView);

      pager.innerHTML = '';
      if (pages <= 1) {
        pager.hidden = true;
        slider.dataset.pageScroll = '';
        slider.dataset.pages = '';
        return;
      }

      pager.hidden = false;

      const pageScroll = Math.max(1, Math.round(cardsPerView * step));
      slider.dataset.pageScroll = String(pageScroll);
      slider.dataset.pages = String(pages);

      for (let i = 0; i < pages; i++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'tab-carousel__page-dot' + (i === 0 ? ' is-active' : '');
        b.dataset.pageIndex = String(i);
        b.setAttribute('aria-label', `Go to page ${i + 1}`);
        pager.appendChild(b);
      }

      if (!slider.dataset.pagerBound) {
        slider.dataset.pagerBound = 'true';
        slider.addEventListener('scroll', () => this.syncMainPager(slider), { passive: true });
      }

      this.syncMainPager(slider);
    }

    syncMainPager(slider) {
      const content = slider.closest('.tab-carousel__content');
      const pager = content?.querySelector('[data-main-pager]');
      if (!pager || pager.hidden) return;

      const pageScroll = parseFloat(slider.dataset.pageScroll || '0') || slider.clientWidth || 1;
      const pages = parseInt(slider.dataset.pages || '0', 10) || 0;
      if (!pages) return;

      const maxPage = pages - 1;
      const current = Math.min(maxPage, Math.max(0, Math.round(slider.scrollLeft / pageScroll)));

      const dots = pager.querySelectorAll('.tab-carousel__page-dot');
      dots.forEach((d, i) => d.classList.toggle('is-active', i === current));
    }

    onMainPagerDotClick(dotBtn) {
      const content = dotBtn.closest('.tab-carousel__content');
      const slider = content?.querySelector('[data-main-slider]');
      if (!slider) return;

      const pageIndex = parseInt(dotBtn.dataset.pageIndex || '0', 10) || 0;
      const pageScroll = parseFloat(slider.dataset.pageScroll || '0') || slider.clientWidth || 1;

      const maxScroll = slider.scrollWidth - slider.clientWidth;
      const target = Math.min(maxScroll, Math.max(0, pageIndex * pageScroll));
      slider.scrollTo({ left: target, behavior: 'smooth' });

      setTimeout(() => this.syncMainPager(slider), 120);
    }

    /* ---------- MINI GALLERY (desktop) ---------- */
    onGalleryDotClick(dotBtn) {
      const gallery = dotBtn.closest('.tab-carousel__gallery');
      const track = gallery?.querySelector('[data-gallery-track]');
      if (!track) return;

      const idx = parseInt(dotBtn.dataset.dotIndex || '0', 10) || 0;
      track.scrollTo({ left: track.clientWidth * idx, behavior: 'smooth' });
      setTimeout(() => this.syncGalleryDots(gallery), 120);
    }

    attachGalleryScrollSync() {
      this.container.querySelectorAll('.tab-carousel__gallery').forEach((gallery) => {
        const track = gallery.querySelector('[data-gallery-track]');
        if (!track || track.dataset.dotBound) return;
        track.dataset.dotBound = 'true';
        track.addEventListener('scroll', () => this.syncGalleryDots(gallery), { passive: true });
      });
    }

    syncGalleryDots(gallery) {
      const track = gallery.querySelector('[data-gallery-track]');
      const dots = gallery.querySelectorAll('.tab-carousel__gallery-dot');
      if (!track || !dots.length) return;

      const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
    }

    scrollCardGallery(btn) {
      const wrapper = btn.closest('.tab-carousel__gallery');
      const track = wrapper?.querySelector('[data-gallery-track]');
      if (!track) return;

      const dir = btn.classList.contains('left') ? -1 : 1;
      const pageWidth = track.clientWidth;
      const maxScroll = track.scrollWidth - track.clientWidth;

      if (dir === 1 && (track.scrollLeft + 10) >= maxScroll) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else if (dir === -1 && track.scrollLeft <= 10) {
        track.scrollTo({ left: Math.max(0, maxScroll), behavior: 'smooth' });
      } else {
        track.scrollBy({ left: pageWidth * dir, behavior: 'smooth' });
      }

      setTimeout(() => this.syncGalleryDots(wrapper), 120);
    }

    /* ---------- QUICK VIEW ---------- */
    openModal(uniqueId) {
      const script = document.getElementById(`data-${uniqueId}`);
      if (!script) return;

      let data;
      try { data = JSON.parse(script.textContent); }
      catch (err) { console.error('Quick view JSON parse failed:', err); return; }

      this._lastFocus = document.activeElement;

      const titleEl = this.modal.querySelector('.qv-title');
      const descEl = this.modal.querySelector('.qv-desc');
      const linkEl = this.modal.querySelector('.qv-link');

      const priceCurrent = this.modal.querySelector('.qv-price-current');
      const priceCompare = this.modal.querySelector('.qv-price-compare');

      if (titleEl) titleEl.textContent = data.title || '';
      if (descEl) descEl.textContent = data.description || '';
      if (linkEl) linkEl.href = data.url || '#';

      if (priceCurrent) priceCurrent.textContent = data.price_formatted || '';
      if (priceCompare) {
        const showCompare = !!data.on_sale && data.compare_at_price_formatted;
        priceCompare.hidden = !showCompare;
        priceCompare.textContent = showCompare ? data.compare_at_price_formatted : '';
      }

      const variantInput = this.modal.querySelector('.qv-variant-id');
      if (variantInput) variantInput.value = data.variant_id || '';

      const stockMsg = this.modal.querySelector('.qv-stock-msg');
      if (stockMsg) {
        if (data.is_low_stock) {
          stockMsg.hidden = false;
          stockMsg.textContent = `🔥 Only ${data.inventory_qty} left!`;
        } else {
          stockMsg.hidden = true;
          stockMsg.textContent = '';
        }
      }

      const btn = this.modal.querySelector('.qv-add-btn');
      const btnSpan = btn?.querySelector('span');
      if (btn) {
        if (data.available) {
          btn.disabled = false;
          if (btnSpan) btnSpan.textContent = 'Add to Cart';
        } else {
          btn.disabled = true;
          if (btnSpan) btnSpan.textContent = 'Sold Out';
        }
      }

      const track = this.modal.querySelector('.qv-slider-track');
      if (track) {
        track.innerHTML = '';
        const images = Array.isArray(data.images) ? data.images.filter(Boolean) : [];
        if (images.length) {
          images.forEach((src) => {
            const img = document.createElement('img');
            img.className = 'qv-slide';
            img.loading = 'lazy';
            img.alt = data.title || '';
            img.src = src;
            track.appendChild(img);
          });
        } else {
          const fallback = document.createElement('div');
          fallback.style.width = '100%';
          fallback.style.height = '100%';
          fallback.style.background = '#eee';
          track.appendChild(fallback);
        }
        track.scrollLeft = 0;
      }

      this.modal.classList.add('open');
      document.documentElement.classList.add('tab-carousel--modal-open');
      document.body.classList.add('tab-carousel--modal-open');

      this.modal.querySelector('.qv-close')?.focus?.();
    }

    closeModal() {
      this.modal.classList.remove('open');
      document.documentElement.classList.remove('tab-carousel--modal-open');
      document.body.classList.remove('tab-carousel--modal-open');

      if (this._lastFocus && this._lastFocus.focus) {
        try { this._lastFocus.focus(); } catch (_) {}
      }
      this._lastFocus = null;
    }
  }

  function initWithin(root = document) {
    const nodes = root.querySelectorAll?.('[data-tabbed-carousel]') || [];
    nodes.forEach((container) => {
      const key = container.id || container;
      if (INSTANCES.has(key)) return;
      const instance = new TabbedCarousel(container);
      if (instance?.container && instance?.modal) INSTANCES.set(key, instance);
    });
  }

  function destroyWithin(root) {
    const nodes = root.querySelectorAll?.('[data-tabbed-carousel]') || [];
    nodes.forEach((container) => {
      const key = container.id || container;
      const instance = INSTANCES.get(key);
      if (instance) {
        instance.destroy();
        INSTANCES.delete(key);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => initWithin(document));
  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
})();
